import React, { useCallback, useEffect, useRef, useState } from 'react';

import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';

import {
  useAemContent,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { formatMilesInThousands } from '../../shared/utils/format/format';

import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../shared/constants/cms';

import './tread-life-estimate.scss';

interface TreadLifeEstimateProps {
  className?: string;
  medianMileage: number | null | undefined;
  minMileage: number | null | undefined;
}

const MIN_WIDTH = 235;

function TreadLifeEstimate(props: TreadLifeEstimateProps) {
  const { className, medianMileage, minMileage } = props;
  const fallbackMessage =
    'Tread life is an estimate based on actual mileage for this tire on your vehicle model, in your area, with proper maintenance.';
  const { treadwellPreferences } = useTreadwellPreferences();
  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPdp
  );
  const winterMessage =
    aemTooltipsContent?.[AEM_CONTENT_IDS.winterTreadlifeEstimate]?.tipText;
  const allSeasonMessage =
    aemTooltipsContent?.[AEM_CONTENT_IDS.allSeasonTreadlifeEstimate]?.tipText;
  const tooltipMessage = treadwellPreferences?.showWinterTires
    ? winterMessage
    : allSeasonMessage;
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldAbbreviate, setShouldAbbreviate] = useState(false);

  const checkShouldAbbreviate = useCallback(() => {
    if (containerRef.current) {
      const { width: containerRefWidth } =
        containerRef.current.getBoundingClientRect();

      setShouldAbbreviate(containerRefWidth < MIN_WIDTH);
    }
  }, []);

  useEffect(() => {
    checkShouldAbbreviate();
    window.addEventListener('resize', checkShouldAbbreviate);

    return () => window.removeEventListener('resize', checkShouldAbbreviate);
  }, [checkShouldAbbreviate]);
  if (!medianMileage || medianMileage === 0) {
    return null;
  }

  return (
    <div ref={containerRef} styleName="wrapper">
      <Typography
        className={className}
        size="small"
        styleName="estimate"
        tag="div"
      >
        {`${formatMilesInThousands(minMileage)}-${formatMilesInThousands(
          medianMileage
        )}
        tread life ${shouldAbbreviate ? 'est.' : 'estimate'}`}

        <Tooltip
          content={tooltipMessage || fallbackMessage}
          styleName="tooltip"
        />
      </Typography>
    </div>
  );
}

export default TreadLifeEstimate;
